
import { Vue, Component, Prop } from 'vue-property-decorator';
import moment from 'moment';

import CREATE_INVOICE from '@/apollo/invoice/CreateInvoice.gql';
import UPDATE_INVOICE from '@/apollo/invoice/UpdateInvoice.gql';
import UPDATE_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/UpdateProjectActivityTimeInvoice.gql';

import InvoiceTable from '@/components/invoice/InvoiceTable.vue';
import InvoiceDetails from '@/components/invoice/InvoiceDetail.vue';
import { NOTIFICATION } from '@/entities/Events';
import Message from '@/data/notificationsMessages';
import { User } from '@/generated/graphql';
import UiButton from '@/components/button/ui-button.vue';

@Component({
	components: {
		InvoiceDetails,
		InvoiceTable,
		UiButton,
	},
})
export default class Step2 extends Vue {
	@Prop({ type: Object, default: () => ({}) }) record!: any;
	@Prop({ type: Object, required: true }) readonly currentUser!: User;

	invoiceInfo = {};
	totalHours = '';
	totalAmount = 0;
	headers = [
		{ text: 'Visit', value: 'visit' },
		{ text: 'Rate', value: 'rate' },
		{ text: 'Hours', value: 'hours' },
		{ text: 'Total amount', value: 'totalAmount' },
	];
	isLoading = false;
	invoiceData = [];

	parseTimeToMinutes(timeString: string) {
		let totalMinutes = 0;
		const parts = timeString.match(/(\d+)(h|m)/g);

		if (parts) {
			for (const part of parts) {
				const value = parseInt(part);
				if (part.includes('h')) {
					totalMinutes += value * 60;
				} else if (part.includes('m')) {
					totalMinutes += value;
				}
			}
		}

		return totalMinutes;
	}

	created() {
		const { role } = this.currentUser;
		const labelKey = role === 'ADMIN' ? 'Resource' : 'Company';
		const idKey = role === 'ADMIN' ? 'resource_id' : 'company_id';
		
		const totalMinutes = this.record.selectedItems.reduce((total, item) => {
			return total + this.parseTimeToMinutes(item.hours);
		}, 0);

		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		this.totalHours = `${hours}h ${minutes}m`;

		const totalAmountDue = this.record.selectedItems.reduce((total, item) => {
			const amount = parseFloat(item.totalAmount.replace('$', ''));
			return total + amount
		}, 0);
		 this.totalAmount = totalAmountDue.toFixed(2);

		this.invoiceInfo = {
			[labelKey]: this.record[idKey].label,
			'Client': this.record.project_id.label,
			'Reference Number': this.record.invoice_ref,
			'External Invoice Link': this.record.file ? this.record.file.key.split('/')[1] :this.record.external_link,
			'Total hours': `${this.totalHours}`,
			'Total Amount before taxes': `${this.record.currency} $${totalAmountDue}`,
			'Submission Date': this.formatDate(
				this.record.invoice_submission_date
			),
		};
		
		this.invoiceData = this.record.selectedItems.map((item) => ({

			// date: item.date,
			visit: item.visit,
			rate: item.rate,
			hours: item.hours,
			totalAmount: item.totalAmount,
		}));
	}

	calculateTotalHours(items) {
		return (
			items.reduce(
				(total, item) => total + this.parseHours(item.hours),
				0
			) + 'h'
		);
	}

	calculateTotalAmount(items) {
		return items
			.reduce((total, item) => {
				const amount = parseFloat(item.totalAmount.replace('$', ''));
				return total + amount;
			}, 0)
			.toFixed(2);
	}

	formatDate(dateString) {
		return moment(dateString).format('MMM D, YYYY');
	}
	parseHours(hourString) {
		return parseFloat(hourString);
	}

	convertTimeToDecimal(timeString) {
  const [hoursStr, minutesStr] = timeString.split('h ');

  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  const decimalHours = hours + (minutes / 60);
  return decimalHours.toFixed(3);
}

	async submitInvoice() {
		const { role } = this.currentUser;
		try {
			this.isLoading = true;
			 const finalConvertedHours = this.convertTimeToDecimal(this.totalHours);
			const mutate: any = this.$route.params.id
				? {
						mutation: UPDATE_INVOICE,
						variables: {
							set: {
								company_id: this.record.company_id.id,
								project_id: this.record.project_id.id,
								status: 'PENDING',
								external_link: this.record.external_link,
								invoice_ref: this.record.invoice_ref,
								total_hours: finalConvertedHours,
								total_amount: Number(this.totalAmount),
								file: this.record.file,
								rate: parseInt(this.record.rate || 0),
								submit_by:
									role === 'ADMIN'
										? this.record.resource_id.id
										: this.currentUser.id,
							},

							id: this.$route.params.id,
						},
				  }
				: {
						mutation: CREATE_INVOICE,
						variables: {
							object: {
								company_id: this.record.company_id.id,
								project_id: this.record.project_id.id,
								status: 'PENDING',
								external_link: this.record.external_link,
								invoice_ref: this.record.invoice_ref,
								total_hours: finalConvertedHours,
								total_amount: Number(this.totalAmount),
								rate: parseInt(this.record.rate || 0),
								file: this.record.file,
								submit_by:
									role === 'ADMIN'
										? this.record.resource_id.id
										: this.currentUser.id,
							},
						},
				  };

			const result: any = await this.$apollo.mutate(mutate);
			const invoiceId = result.data[Object.keys(result.data)[0]].id;
			const updatePromises = this.record.selectedItems.map((activityId) => {
				this.$apollo.mutate({
					mutation: UPDATE_PROJECT_ACTIVITY_TIME,
					variables: {
						obj: { invoice_id: invoiceId },
						ids: activityId.acitivity_time_id,
					},
				})
			}
			);
			await Promise.all(updatePromises);
			this.$eventBus.emit(
				NOTIFICATION,
				Message.successMessage(`Invoice Successfully Submitted`)
			);
			this.$router.push('/invoice');
			this.isLoading = false;
		} catch (error) {
			this.isLoading = false;
		}
	}

	onEditClick(step) {
		this.$router.push('?action=edit');
		this.$emit('editStepper', step);
	}
}
